import React, { useEffect, useRef, useState, useContext } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { addSignature, getSignature } from '../../../store/actions/DocumentsAction';
import { useAppDispatch } from '../../../store';
import { useTranslation } from 'react-i18next';
import Button from '../../shared/TailwindComponents/Button';
import { ThemeContext } from '../../../contexts/ThemeContext';
import { getUserID } from '../../../store/actions/ChatAction';

interface SignatureModalProps {
  onSave: (signature: string) => void;
  onConfirm: (signatureFile: File) => void;
}
const SignatureModal: React.FC<SignatureModalProps> = ({
  onSave,
  onConfirm,
}) => {
  const signatureCanvasRef = useRef<SignatureCanvas>(null);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [signatureSVGContent, setSignatureSVGContent] = useState<string | null>(null);
  const userId = getUserID();

  const [isLoadingSignature, setIsLoadingSignature] = useState<boolean>(true);
  const [errorLoading, setErrorLoading] = useState(false);

  const { theme } = useContext(ThemeContext);
  const isDarkMode = theme === 'dark';

  const loadImage = async () => {
    try {
      setIsLoadingSignature(true);
      const sign_url = await dispatch(getSignature());
      if (!sign_url) {
        setIsEditing(true);
        setSignatureSVGContent(null);
      } else {
        const response = await fetch(sign_url, {
          headers: {
            Accept: 'image/svg+xml',
          },
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const svgText = await response.text();
        if (svgText) {
          setSignatureSVGContent(svgText);
          setIsEditing(false);
        } else {
          setSignatureSVGContent(null);
          setIsEditing(true);
        }
      }
    } catch (error) {
      setErrorLoading(true);
      setSignatureSVGContent(null);
      setIsEditing(true);
    } finally {
      setIsLoadingSignature(false);
    }
  };

  useEffect(() => {
    loadImage();
  }, []);

  const handleClear = () => {
    signatureCanvasRef.current?.clear();
  };

  const handleSave = async () => {
    if (!signatureCanvasRef.current) {
      alert('Signature pad not initialized.');
      return;
    }
    if (signatureCanvasRef.current.isEmpty()) {
      alert(t('Please provide a signature first.'));
      return;
    }

    const signaturePad = signatureCanvasRef.current.getSignaturePad();
    const signatureData = signaturePad.toData();
    const canvas = signatureCanvasRef.current.getCanvas();

    const signatureSVG = signatureDataToSVG(signatureData, {
      width: canvas.width,
      height: canvas.height,
      penColor: signaturePad.penColor || (isDarkMode ? 'white' : 'black'),
    });

    const blob = new Blob([signatureSVG], { type: 'image/svg+xml' });
    const signatureFile = new File([blob], `${userId}_signature.svg`, {
      type: 'image/svg+xml',
    });

    onSave(signatureSVG);
    onConfirm(signatureFile);
    localStorage.setItem('userSignature', signatureSVG);

    try {
      await dispatch(addSignature(signatureFile));
      setIsEditing(false);
      loadImage();
    } catch (error) {
    }
  };

  return (
    <div className="flex flex-col h-full justify-start text-start rounded-lg space-y-4">
      <div className='space-y-2'>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            {t('Digital signature')}</h3>
          {!isLoadingSignature && signatureSVGContent && !isEditing && (
            <Button variant="primary" size="small" onClick={() => setIsEditing(true)}>
              {t('Edit signature')}
            </Button>
          )}
        </div>

        <span className="text-sm text-gray-500 dark:text-gray-400">
          {t('Set your digital signature for e-signing documents and forms.')}
        </span>
      </div>
      <hr className="my-4 border-gray-200 dark:border-gray-700" />
      <div className="w-[500px] h-[200px] relative animate-fade-in-up">
        {isLoadingSignature && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-full h-full bg-gray-200 dark:bg-gray-700 animate-fadePulse rounded-md" />
          </div>
        )}

        {!isLoadingSignature && errorLoading && (
          <div className="flex items-center justify-center w-full h-full bg-danger-100 text-danger-600 p-2 rounded-md">
            {t('Failed to load signature. Please create a new one.')}
          </div>
        )}

        {!isLoadingSignature && signatureSVGContent && !isEditing && (
          <div
            className="rounded bg-white dark:bg-gray-800 border-1 border-gray-300 dark:border-gray-600 h-[200px] w-[500px] transition-all duration-200 flex items-center justify-center border border-gray-100 dark:border-gray-700 overflow-hidden"
            dangerouslySetInnerHTML={{ __html: signatureSVGContent }}
          />
        )}

        {!isLoadingSignature && (isEditing || !signatureSVGContent) && (
          <SignatureCanvas
            penColor={'black'}
            ref={signatureCanvasRef}
            canvasProps={{
              width: 500,
              height: 200,
              className:
                'rounded-lg bg-white dark:bg-gray-700 border-1 border-gray-200 dark:border-gray-600 border border-gray-200 dark:border-gray-600 transition-all duration-200',
            }}
            minWidth={1}
            maxWidth={3}
            throttle={16}
          />
        )}
      </div>

      {!isLoadingSignature && (isEditing || !signatureSVGContent) && (
        <div className="flex space-x-2">
          <Button variant="neutral" size="small" onClick={handleClear}>
            {t('Clear')}
          </Button>
          <Button variant="primary" size="small" onClick={handleSave}>
            {t('Save')}
          </Button>
        </div>
      )}
    </div>
  );
};

function signatureDataToSVG(
  signatureData: Array<Array<{ x: number; y: number }>>,
  options: { width: number; height: number; penColor: string }
): string {
  const { width, height, penColor } = options;
  let svgContent = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">`;
  svgContent += `<g fill="none" stroke="${penColor}" stroke-width="2">`;

  signatureData.forEach((stroke) => {
    if (stroke.length > 0) {
      let pathData = `M ${stroke[0].x.toFixed(3)} ${stroke[0].y.toFixed(3)}`;
      for (let i = 1; i < stroke.length; i++) {
        pathData += ` L ${stroke[i].x.toFixed(3)} ${stroke[i].y.toFixed(3)}`;
      }
      svgContent += `<path d="${pathData}" />`;
    }
  });

  svgContent += '</g></svg>';
  return svgContent;
}

export default SignatureModal;
